/**
 * AppointmentsBook — the Appointments process home (route `appointments`,
 * rail "Appointments"): a location-scoped DAY BOOK of booked appointments.
 *
 * 2026-08-06 rebuild (operator: "die Einrückung ist nicht schön und es werden
 * zu wenig Daten angezeigt … Actions eher in ein Action-Menu … eine mächtige
 * Filterkomponente, idealerweise eine geshared bestehende … Location-Select
 * unsere Select-Komponente … UTM hinter Tooltip oder 'mehr Informationen'"):
 *
 *  · One 24px gutter for every band (the old screen sat at 32px while the page
 *    header sat at 24px — that was the visible mis-indent).
 *  · A dense TABLE instead of three-field rows: time+length, customer with
 *    contact, service+channel, staff, location, booking source (UTM behind a
 *    tooltip), answered booking questions, status.
 *  · Row actions collapse into the shared kebab (`RowActions`) — no inline
 *    button cluster.
 *  · Filtering runs through the shared `FilterToolbar` composite (free text +
 *    multi-select facets with counts + removable chips), which this screen is
 *    the first consumer of; the location scope is the design-system `Dropdown`,
 *    not a browser <select>.
 *  · The full record — every answer, the whole attribution block, notes — lives
 *    in a `<Drawer>` (overlay standard: one record ⇒ Drawer).
 *
 * Data comes from listAppointments (seed-world documents through the
 * appointments layer; see DataSourceStrip). Status model: booked → confirmed →
 * checked-in → served / no-show / cancelled / rescheduled (APT-03).
 */
import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Button, Dropdown, Icon, Pagination, T, Tooltip } from "@v0y4ge/jrni-ui";
import { useT } from "@v0y4ge/i18n-runtime";
import { Screen, PageHeader } from "../../shell/chrome";
import { useNav } from "../../shell/router";
import { Drawer } from "../../shell/Overlay";
import { DataSourceStrip } from "../../shell/opSource";
import { StatTile, EmptyState, LoadState, RowActions, FilterToolbar, type FilterFacet } from "../../shell/composites";
import { thStyle, tdStyle } from "../events/EventCard";
import { useIsMobile } from "../../shell/useResponsive";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import BookAppointmentSheet from "./BookAppointmentSheet";
import {
  cancelAppointment, checkInAppointment, listAppointments, listApptSlots,
  markAppointmentNoShow, rescheduleAppointment,
  type Appointment, type ApptLocationRef, type ApptSlot, type ApptStatus,
} from "./appointments-api";

const STATUS_META: Record<ApptStatus, { label: string; fg: string; bg: string }> = {
  booked:        { label: "Booked",       fg: "#4B5563", bg: "#F3F4F6" },
  confirmed:     { label: "Confirmed",    fg: "#1D4ED8", bg: "#EFF4FF" },
  "checked-in":  { label: "Checked in",   fg: "#027D47", bg: "#EAF7F0" },
  served:        { label: "Served",       fg: "#374151", bg: "#EDEFF2" },
  "no-show":     { label: "No-show",      fg: "#B45309", bg: "#FEF4E6" },
  cancelled:     { label: "Cancelled",    fg: "#B91C1C", bg: "#FDECEC" },
  rescheduled:   { label: "Rescheduled",  fg: "#6D28D9", bg: "#F2EEFC" },
};
const CHANNEL_LABEL: Record<string, string> = { inPerson: "In person", phone: "Phone", video: "Video" };
const VIA_LABEL: Record<string, string> = { website: "Website", mobile: "Mobile app", phone: "Phone", email: "Email", staff: "Booked by staff", queue: "From a queue", event: "From an event", journey: "Self-service", import: "Imported" };
const OPEN_STATUSES: ApptStatus[] = ["booked", "confirmed"];
const ALL_LOCATIONS = "__all";
const todayIso = () => new Date().toISOString().slice(0, 10);
const minutesOf = (a: Appointment) => {
  if (a.durationMin) return a.durationMin;
  const [sh, sm] = a.start.split(":").map(Number);
  const [eh, em] = (a.end || a.start).split(":").map(Number);
  return Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
};
/** One header-cell style for the whole table — sentence case, never uppercase
 *  (the mixed casing came from a row-level text-transform that the sortable
 *  header buttons did not inherit). */
const thBase: CSSProperties = { ...thStyle, background: T.n6, color: T.soft, fontSize: 12, fontWeight: 600, textTransform: "none", letterSpacing: 0, whiteSpace: "nowrap" };

/* ── Reschedule (Drawer: a list that scrolls — overlay standard) ─────────── */
/** Exported so the record page can offer the same flow without a second copy. */
export function RescheduleDrawer({ appt, onClose, onDone }: { appt: Appointment; onClose: () => void; onDone: (a: Appointment) => void }) {
  const t = useT();
  const [date, setDate] = useState(appt.date);
  const [slots, setSlots] = useState<ApptSlot[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const days = useMemo(() => { const out: string[] = []; for (let i = 0; i < 7; i++) { const d = new Date(); d.setDate(d.getDate() + i); out.push(d.toISOString().slice(0, 10)); } return out; }, []);
  useEffect(() => {
    setSlots(null); setErr(null);
    listApptSlots({ service: appt.service, date, locationId: appt.locationId })
      .then((r) => setSlots(r.slots)).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [appt.service, appt.locationId, date]);
  const pickSlot = async (s: ApptSlot) => {
    setSaving(true); setErr(null);
    try { const a = await rescheduleAppointment(appt.id, { date, start: s.start, end: s.end, staffId: s.staffId, staffName: s.staffName }); onDone(a); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); setSaving(false); }
  };
  return (
    <Drawer panel="appt-reschedule" testid="appt-reschedule-drawer" width={460} onClose={onClose}
      icon={<Icon.calendar size={16} />}
      title={`${t("appointments.book.reschedule", "Reschedule")} · ${appt.customer.name}`}
      sub={`${appt.service} — ${t("appointments.book.currently", "currently")} ${appt.date} ${appt.start}`}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {days.map((d) => (
          <button key={d} type="button" onClick={() => setDate(d)}
            style={{ minHeight: 34, padding: "0 11px", borderRadius: 8, border: `1.5px solid ${date === d ? T.p1 : T.n5}`, background: date === d ? T.p4 : T.white, fontSize: 12, fontWeight: 600, fontFamily: T.font, cursor: "pointer" }}>
            {d === todayIso() ? t("appointments.book.today", "Today") : d.slice(5)}
          </button>
        ))}
      </div>
      {err ? <div style={{ fontSize: 12.5, color: T.red1 }}>{err}</div>
        : !slots ? <div style={{ fontSize: 12.5, color: T.soft }}>{t("appointments.sheet.loadingSlots", "Loading availability…")}</div>
        : slots.length === 0 ? <div style={{ fontSize: 12.5, color: T.soft }}>{t("appointments.sheet.noSlots", "No open times for this day — try another day.")}</div> : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(104px, 1fr))", gap: 8 }}>
          {slots.map((s) => (
            <button key={s.start + s.staffId} type="button" disabled={saving} onClick={() => { void pickSlot(s); }} data-testid="appt-resched-slot"
              style={{ minHeight: 46, padding: "8px 6px", borderRadius: 8, border: `1.5px solid ${T.n5}`, background: T.white, cursor: "pointer", fontFamily: T.font, opacity: saving ? 0.6 : 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{s.start}</div>
              <div style={{ fontSize: 10.5, color: T.soft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.staffName}</div>
            </button>
          ))}
        </div>
      )}
    </Drawer>
  );
}

/* ── Attribution ─────────────────────────────────────────────────────────── */
function SourceCell({ appt }: { appt: Appointment }) {
  const t = useT();
  const via = VIA_LABEL[appt.bookedVia || appt.origin || "staff"] || appt.bookedVia || "—";
  const at = appt.attribution;
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 12.5, color: T.n2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{via}</span>
        {appt.bookedFrom ? (
          <span style={{ display: "block", fontSize: 11, color: T.soft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 170 }}>{appt.bookedFrom}</span>
        ) : null}
      </span>
      {at ? (
        <Tooltip position="top" content={
          <span style={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "left" }}>
            <span style={{ fontWeight: 700 }}>{t("appointments.book.attribution", "Attribution")}</span>
            <span>utm_id: {at.id || "—"}</span>
            <span>utm_source: {at.source || "—"}</span>
            <span>utm_medium: {at.medium || "—"}</span>
            <span>utm_campaign: {at.campaign || "—"}</span>
            <span>utm_content: {at.content || "—"}</span>
            <span style={{ opacity: 0.75, marginTop: 2 }}>{t("appointments.book.openForMore", "Open the appointment for the full source")}</span>
          </span>
        }>
          <span tabIndex={0} aria-label={t("appointments.book.attribution", "Attribution")}
            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: 999, background: T.p4, color: T.p1, cursor: "help", flexShrink: 0 }}>
            <Icon.link size={11} />
          </span>
        </Tooltip>
      ) : null}
    </span>
  );
}


/* ── Screen ──────────────────────────────────────────────────────────────── */
export default function AppointmentsBook() {
  const t = useT();
  const nav = useNav();
  const isMobile = useIsMobile();
  const canBook = can("appointments.book");
  const canCheckin = can("appointments.checkin");
  const [horizon, setHorizon] = useState(30);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [data, setData] = useState<{ locationId?: string; locations: ApptLocationRef[]; appointments: Appointment[] } | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [sort, setSort] = useState<{ key: "time" | "customer" | "status"; dir: 1 | -1 }>({ key: "time", dir: 1 });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [resched, setResched] = useState<Appointment | null>(null);

  /* One long forward-looking list, not a day at a time (operator, 2026-08-06).
     `listAppointments` is a per-day read, so the horizon is walked day by day
     and merged; ids de-duplicate the overlap between the seed reads. */
  const refresh = useCallback(() => {
    const all = locationId === ALL_LOCATIONS;
    const days = Array.from({ length: horizon }, (_, i) => { const d = new Date(); d.setDate(d.getDate() + i); return d.toISOString().slice(0, 10); });
    listAppointments({ date: days[0], locationId: all ? null : locationId })
      .then(async (first) => {
        const locIds = all ? (first.locations || []).map((l) => l.id) : [first.locationId];
        const reads = await Promise.all(locIds.flatMap((id) => days.map((d) => (
          d === days[0] && id === first.locationId ? Promise.resolve(first) : listAppointments({ date: d, locationId: id })
        ))));
        const seen = new Set<string>();
        const appointments = reads.flatMap((r) => r.appointments).filter((a) => (seen.has(a.id) ? false : (seen.add(a.id), true)));
        return { locationId: first.locationId, locations: first.locations, appointments };
      })
      .then((r) => { setData(r); setLoadErr(null); if (!locationId) setLocationId(r.locationId ?? null); })
      .catch((e) => { setLoadErr(e instanceof Error ? e.message : String(e)); });
  }, [horizon, locationId]);
  useEffect(() => { refresh(); }, [refresh]);

  const locations = data?.locations ?? [];
  const locName = useCallback((id: string) => locations.find((l) => l.id === id)?.name ?? id, [locations]);
  const appts = data?.appointments ?? [];

  /* Facets — options and counts come from the loaded day, so an empty facet
     never offers a value that would return nothing. */
  const facets: FilterFacet[] = useMemo(() => {
    const bucket = (get: (a: Appointment) => string[] | string | null | undefined) => {
      const m = new Map<string, number>();
      appts.forEach((a) => { const v = get(a); (Array.isArray(v) ? v : [v]).forEach((x) => { if (x) m.set(x, (m.get(x) ?? 0) + 1); }); });
      return m;
    };
    const opts = (m: Map<string, number>, label: (v: string) => string) =>
      [...m.entries()].sort((x, y) => y[1] - x[1] || label(x[0]).localeCompare(label(y[0]))).map(([value, count]) => ({ value, label: label(value), count }));
    const out: FilterFacet[] = [
      { id: "status", label: t("appointments.book.status", "Status"), icon: <Icon.check size={13} />, options: opts(bucket((a) => a.status), (v) => STATUS_META[v as ApptStatus]?.label ?? v) },
      { id: "staff", label: t("appointments.book.staff", "Staff"), icon: <Icon.user size={13} />, options: opts(bucket((a) => a.staffName), (v) => v) },
      { id: "service", label: t("appointments.book.service", "Service"), icon: <Icon.tags size={13} />, options: opts(bucket((a) => a.service), (v) => v) },
      { id: "channel", label: t("appointments.book.channel", "Channel"), icon: <Icon.video size={13} />, options: opts(bucket((a) => a.channel), (v) => CHANNEL_LABEL[v] ?? v) },
      { id: "via", label: t("appointments.book.source", "Source"), icon: <Icon.link size={13} />, options: opts(bucket((a) => a.bookedVia || a.origin), (v) => VIA_LABEL[v] ?? v) },
      { id: "campaign", label: t("appointments.book.campaign", "Campaign"), icon: <Icon.chart size={13} />, options: opts(bucket((a) => a.attribution?.campaign), (v) => v) },
      { id: "question", label: t("appointments.book.answeredQuestion", "Answered question"), icon: <Icon.help size={13} />, options: opts(bucket((a) => (a.answers ?? []).map((x) => x.question)), (v) => v) },
    ];
    const locBucket = bucket((a) => a.locationId);
    if (locBucket.size > 1) out.splice(4, 0, { id: "location", label: t("appointments.book.location", "Location"), icon: <Icon.loc size={13} />, options: opts(locBucket, locName) });
    return out.filter((f) => f.options.length > 1 || (selected[f.id]?.length ?? 0) > 0);
  }, [appts, locName, selected, t]);

  const matches = useCallback((a: Appointment) => {
    const sel = selected;
    const has = (id: string, v: string | null | undefined) => !(sel[id]?.length) || (!!v && sel[id].includes(v));
    if (!has("status", a.status)) return false;
    if (!has("staff", a.staffName)) return false;
    if (!has("service", a.service)) return false;
    if (!has("channel", a.channel)) return false;
    if (!has("location", a.locationId)) return false;
    if (!has("via", a.bookedVia || a.origin)) return false;
    if (!has("campaign", a.attribution?.campaign)) return false;
    const qSel = sel.question ?? [];
    if (qSel.length && !(a.answers ?? []).some((x) => qSel.includes(x.question))) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const hay = [
      a.customer.name, a.customer.email, a.customer.phone, a.ref, a.service, a.staffName, locName(a.locationId),
      a.notes, a.bookedVia, a.bookedFrom, CHANNEL_LABEL[a.channel], STATUS_META[a.status]?.label,
      a.attribution?.source, a.attribution?.medium, a.attribution?.campaign, a.attribution?.content, a.attribution?.term, a.attribution?.landing,
      ...(a.answers ?? []).flatMap((x) => [x.question, x.answer]),
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  }, [selected, query, locName]);

  const rows = useMemo(() => {
    const out = appts.filter(matches);
    const cmp = (a: Appointment, b: Appointment) => {
      if (sort.key === "customer") return a.customer.name.localeCompare(b.customer.name);
      if (sort.key === "status") return (STATUS_META[a.status]?.label ?? "").localeCompare(STATUS_META[b.status]?.label ?? "");
      return (a.date + a.start).localeCompare(b.date + b.start) || a.customer.name.localeCompare(b.customer.name);
    };
    return out.sort((a, b) => cmp(a, b) * sort.dir);
  }, [appts, matches, sort]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const pageNo = Math.min(page, totalPages);
  const pageRows = useMemo(() => rows.slice((pageNo - 1) * pageSize, pageNo * pageSize), [rows, pageNo, pageSize]);
  const firstShown = rows.length ? (pageNo - 1) * pageSize + 1 : 0;
  const lastShown = Math.min(pageNo * pageSize, rows.length);
  // Any change to what is being listed returns to page 1 — page 4 of a filter
  // that now matches six rows is an empty table for no reason.
  useEffect(() => { setPage(1); }, [query, selected, horizon, locationId, sort, pageSize]);

  const kpi = useMemo(() => ({
    total: appts.length,
    checkedIn: appts.filter((a) => a.status === "checked-in").length,
    served: appts.filter((a) => a.status === "served").length,
    noShow: appts.filter((a) => a.status === "no-show").length,
  }), [appts]);

  const act = async (id: string, fn: () => Promise<Appointment>, okMsg: string) => {
    setBusyId(id);
    try { await fn(); toast({ tone: "success", title: okMsg }); refresh(); }
    catch (e) { toast({ tone: "danger", title: t("appointments.book.actionFailed", "Couldn't update the appointment"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusyId(null); }
  };

  const toggleFacet = (facetId: string, value: string) => setSelected((prev) => {
    const cur = prev[facetId] ?? [];
    const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    const out = { ...prev, [facetId]: next };
    if (!next.length) delete out[facetId];
    return out;
  });

  const rowMenu = (a: Appointment) => {
    const open = OPEN_STATUSES.includes(a.status);
    const busy = busyId === a.id;
    const items: Array<{ label: string; icon?: ReactNode; onClick?: () => void; danger?: boolean; testid?: string } | "divider"> = [
      { label: t("appointments.book.viewDetails", "View details"), icon: <Icon.eye size={14} />, onClick: () => nav.go("appointment.detail", { id: a.id }), testid: "appt-act-detail" },
    ];
    if (!busy && canCheckin && open) items.push({ label: t("appointments.book.checkIn", "Check in"), icon: <Icon.check size={14} />, onClick: () => { void act(a.id, () => checkInAppointment(a.id), t("appointments.book.checkedInToast", "Checked in")); }, testid: "appt-act-checkin" });
    if (!busy && canCheckin && a.status === "checked-in") items.push({ label: t("appointments.book.markServed", "Mark served"), icon: <Icon.check size={14} />, onClick: () => { void act(a.id, () => checkInAppointment(a.id), t("appointments.book.servedToast", "Marked served")); }, testid: "appt-act-served" });
    if (!busy && canCheckin && open) items.push({ label: t("appointments.book.noShow", "No-show"), icon: <Icon.warn size={14} />, onClick: () => { void act(a.id, () => markAppointmentNoShow(a.id), t("appointments.book.noShowToast", "Recorded no-show")); }, testid: "appt-act-noshow" });
    if (!busy && canBook && open) {
      items.push("divider");
      items.push({ label: t("appointments.book.reschedule", "Reschedule"), icon: <Icon.calendar size={14} />, onClick: () => setResched(a), testid: "appt-act-resched" });
      items.push({ label: t("appointments.book.cancel", "Cancel appointment"), icon: <Icon.x size={14} />, danger: true, onClick: () => { void act(a.id, () => cancelAppointment(a.id), t("appointments.book.cancelledToast", "Appointment cancelled")); }, testid: "appt-act-cancel" });
    }
    return items;
  };


  const th = (key: "time" | "customer" | "status", label: string, extra?: CSSProperties) => (
    <th style={{ ...thBase, ...extra }}>
      <button type="button" onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === 1 ? -1 : 1 }))}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, border: "none", background: "transparent", padding: 0, cursor: "pointer", font: "inherit", color: "inherit", textTransform: "inherit", letterSpacing: "inherit" }}>
        {label}{sort.key === key ? <Icon.chev size={11} dir={sort.dir === 1 ? "down" : "up"} /> : null}
      </button>
    </th>
  );

  return (
    <Screen rail="appt-book">
      <PageHeader
        title={t("appointments.book.appointments", "Appointments")}
        subtitle={t("appointments.book.subtitle", "Every upcoming appointment — check in, reschedule and record outcomes.")}
        breadcrumb={null}
        actions={canBook ? <Button onClick={() => setSheetOpen(true)} data-testid="appt-book-cta"><Icon.plus size={14} /> {t("appointments.book.bookAppointment", "Book appointment")}</Button> : null}
      />
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <div style={{ padding: "14px 24px 0", display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 12 }}>
          <StatTile label={t("appointments.book.upcomingTotal", "Upcoming")} value={kpi.total} icon={<Icon.calendar size={14} />} accent={T.p1} />
          <StatTile label={t("appointments.book.checkedIn", "Checked in")} value={kpi.checkedIn} icon={<Icon.check size={14} />} accent={T.green1} />
          <StatTile label={t("appointments.book.served", "Served")} value={kpi.served} icon={<Icon.user size={14} />} accent={T.n2} />
          <StatTile label={t("appointments.book.noShows", "No-shows")} value={kpi.noShow} icon={<Icon.warn size={14} />} accent={T.orange1} />
        </div>

        <FilterToolbar
          testid="appt-book-filters"
          query={query} onQueryChange={setQuery}
          searchPlaceholder={t("appointments.book.searchPlaceholder", "Search customer, staff, reference, answers, campaign…")}
          facets={facets} selected={selected}
          onToggle={toggleFacet}
          onClearFacet={(id) => setSelected((p) => { const o = { ...p }; delete o[id]; return o; })}
          onClearAll={() => { setSelected({}); setQuery(""); }}
          leading={<>
            <Dropdown
              aria-label={t("appointments.book.range", "Range")}
              testid="appt-book-range"
              value={String(horizon)}
              onChange={(v) => setHorizon(Number(v))}
              style={{ width: 176 }}
              options={[
                { value: "7", label: t("appointments.book.next7", "Next 7 days") },
                { value: "30", label: t("appointments.book.next30", "Next 30 days") },
                { value: "90", label: t("appointments.book.next90", "Next 90 days") },
              ]}
            />
            {locations.length > 1 ? (
            <Dropdown
              aria-label={t("appointments.book.location", "Location")}
              testid="appt-book-location"
              value={locationId}
              onChange={(v) => setLocationId(v)}
              style={{ width: 260 }}
              options={[
                { value: ALL_LOCATIONS, label: t("appointments.book.allLocations", "All locations"), supportingText: t("appointments.book.acrossTheTenant", "Every boutique, branch or campus") },
                ...locations.map((l) => ({ value: l.id, label: l.name })),
              ]}
            />
            ) : locations.length === 1 ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: T.n1 }}><Icon.loc size={13} />{locations[0].name}</span>
            ) : null}
          </>}
        />

        <div style={{ padding: "0 24px 24px" }}>
          <LoadState
            loading={!data && !loadErr}
            error={loadErr}
            empty={rows.length === 0}
            noun={t("appointments.book.appointmentsNoun", "appointments")}
            onRetry={refresh}
            loadingLabel={t("appointments.book.loading", "Loading the book…")}
            testid="appt-book"
            emptyState={
              appts.length === 0 ? (
                <EmptyState variant="card" icon={<Icon.calendar size={24} />}
                  title={t("appointments.book.emptyRange", "No appointments in this range.")}
                  body={canBook ? t("appointments.book.emptyDayCta", "Book the first appointment with the button above.") : undefined} />
              ) : (
                <EmptyState variant="card" icon={<Icon.filter size={24} />}
                  title={t("appointments.book.noMatches", "No appointments match these filters")}
                  body={t("appointments.book.noMatchesBody", "The day holds appointments — they are hidden by the current search and filters.")}
                  action={<Button variant="secondary" onClick={() => { setSelected({}); setQuery(""); }}>{t("shell.composites.clearAll", "Clear all")}</Button>} />
              )
            }
          >
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, width: "fit-content", minWidth: "100%" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: isMobile ? 720 : 980 }}>
                  <thead>
                    <tr style={{ color: T.soft, fontSize: 12, borderBottom: `1px solid ${T.border}` }}>
                      {th("time", t("appointments.book.date", "Date"), { width: 116, borderTopLeftRadius: 9 })}
                      <th style={{ ...thBase, width: 104 }}>{t("appointments.book.time", "Time")}</th>
                      {th("customer", t("appointments.book.customer", "Customer"))}
                      <th style={thBase}>{t("appointments.book.service", "Service")}</th>
                      <th style={thBase}>{t("appointments.book.staff", "Staff")}</th>
                      <th style={thBase}>{t("appointments.book.location", "Location")}</th>
                      <th style={thBase}>{t("appointments.book.source", "Source")}</th>
                      <th style={thBase}>{t("appointments.book.answers", "Answers")}</th>
                      {th("status", t("appointments.book.status", "Status"), { width: 120 })}
                      <th style={{ ...thBase, width: 52, borderTopRightRadius: 9 }} aria-label={t("common.actions.actions", "Actions")} />
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((a, i) => {
                      const st = STATUS_META[a.status];
                      const answers = a.answers ?? [];
                      return (
                        <tr key={a.id} data-testid={`appt-row-${a.ref}`} onClick={() => nav.go("appointment.detail", { id: a.id })}
                          style={{ borderBottom: i === pageRows.length - 1 ? "none" : `1px solid ${T.border}`, cursor: "pointer", opacity: a.status === "cancelled" ? 0.6 : 1 }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = T.n6; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                          <td style={tdStyle}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: T.n1, whiteSpace: "nowrap" }}>
                              {a.date === todayIso() ? t("appointments.book.today", "Today") : new Date(a.date + "T00:00:00").toLocaleDateString(undefined, { day: "2-digit", month: "short" })}
                            </div>
                            <div style={{ fontSize: 11, color: T.soft, whiteSpace: "nowrap" }}>{new Date(a.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" })}</div>
                          </td>
                          <td style={tdStyle}>
                            <div style={{ fontFamily: T.fontMono, fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{a.start}</div>
                            <div style={{ fontSize: 11, color: T.soft, whiteSpace: "nowrap" }}>{a.end} · {minutesOf(a)} min</div>
                          </td>
                          <td style={tdStyle}>
                            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{a.customer.name}</div>
                            <div style={{ fontSize: 11.5, color: T.soft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 210 }}>
                              {a.customer.email || a.customer.phone || a.ref}
                            </div>
                          </td>
                          <td style={tdStyle}>
                            <div style={{ color: T.n1 }}>{a.service}</div>
                            <div style={{ fontSize: 11.5, color: T.soft }}>{CHANNEL_LABEL[a.channel] ?? a.channel} · <span style={{ fontFamily: T.fontMono }}>{a.ref}</span></div>
                          </td>
                          <td style={tdStyle}>{a.staffName ?? t("appointments.book.anyStaff", "Any staff")}</td>
                          <td style={tdStyle}>{locName(a.locationId)}</td>
                          <td style={tdStyle}><SourceCell appt={a} /></td>
                          <td style={tdStyle}>
                            {answers.length === 0 ? <span style={{ color: T.soft }}>—</span> : (
                              <Tooltip position="top" content={
                                <span style={{ display: "flex", flexDirection: "column", gap: 3, textAlign: "left", maxWidth: 280 }}>
                                  {answers.slice(0, 4).map((x, i) => (
                                    <span key={i}><span style={{ opacity: 0.72 }}>{x.question}: </span>{x.answer}</span>
                                  ))}
                                  {answers.length > 4 ? <span style={{ opacity: 0.72 }}>+{answers.length - 4} {t("appointments.book.more", "more")}</span> : null}
                                </span>
                              }>
                                <span tabIndex={0} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 999, background: T.p4, color: T.p1, fontSize: 11.5, fontWeight: 600, cursor: "help" }}>
                                  <Icon.help size={11} />{answers.length}
                                </span>
                              </Tooltip>
                            )}
                          </td>
                          <td style={tdStyle}>
                            <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, color: st.fg, background: st.bg, whiteSpace: "nowrap" }}>{st.label}</span>
                          </td>
                          <td style={{ ...tdStyle, paddingLeft: 0, paddingRight: 10 }} onClick={(e) => e.stopPropagation()}>
                            <RowActions testid={`appt-${a.ref}`} moreHeader={a.ref} moreItems={rowMenu(a)} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "10px 14px", borderTop: `1px solid ${T.border}` }}>
                <span style={{ fontSize: 12.5, color: T.soft }}>
                  {t("appointments.book.showing", "Showing")} {firstShown}–{lastShown} {t("appointments.book.of", "of")} {rows.length}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5, color: T.soft }}>
                    {t("appointments.book.rowsPerPage", "Rows per page")}
                    <Dropdown aria-label={t("appointments.book.rowsPerPage", "Rows per page")} testid="appt-book-pagesize"
                      value={String(pageSize)} onChange={(v) => setPageSize(Number(v))}
                      options={[10, 25, 50, 100].map((n) => ({ value: String(n), label: String(n) }))}
                      style={{ width: 92 }} />
                  </span>
                  {totalPages > 1 ? (
                    <Pagination totalPages={totalPages} currentPage={pageNo} onPageChange={setPage} size="sm" showStatus={false}
                      aria-label={t("appointments.book.pagination", "Appointment pages")} />
                  ) : null}
                </div>
              </div>
            </div>
          </LoadState>
        </div>

        <div style={{ padding: "0 24px 24px" }}>
          <DataSourceStrip screen="appt-book" ops={["listAppointments", "bookAppointment", "rescheduleAppointment", "cancelAppointment", "checkInAppointment", "markAppointmentNoShow", "listApptSlots", "listAppointmentServices"]} />
        </div>
      </div>

      <BookAppointmentSheet open={sheetOpen} onClose={() => setSheetOpen(false)} locationId={locationId === ALL_LOCATIONS ? null : locationId} origin="book" onBooked={() => refresh()} />
      {resched ? <RescheduleDrawer appt={resched} onClose={() => setResched(null)} onDone={(a) => { setResched(null); toast({ tone: "success", title: t("appointments.book.rescheduledToast", "Rescheduled"), subtitle: `${a.date} · ${a.start}` }); refresh(); }} /> : null}
    </Screen>
  );
}
